import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { listActiveAssignmentsApp } from "api/assignments";

import { Button } from "components/ui/Button";
import { AssignModal } from "components/assignments/AssignModal";
import { ReturnModal } from "components/assignments/ReturnModal";
import { ConfirmDialog } from "components/ui/ConfirmDialog";

/* ── Иконки ─────────────────────────────────────────────── */
const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
      clipRule="evenodd"
    />
  </svg>
);
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
      clipRule="evenodd"
    />
  </svg>
);
const ReturnIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
  </svg>
);
const GridIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);
const ListIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
      clipRule="evenodd"
    />
  </svg>
);
const PdfIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M6 2h8l4 4v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
    <path d="M14 2v4h4" />
    <path d="M8 13h5M8 17h3" />
  </svg>
);
const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M3 14a2 2 0 012-2h2v2H5v2h10v-2h-2v-2h2a2 2 0 012 2v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3z" />
    <path d="M7 3a1 1 0 012 0v6.586l1.293-1.293a1 1 0 111.414 1.414l-3.004 3.003a1 1 0 01-1.414 0L4.285 9.707a1 1 0 011.414-1.414L7 9.586V3z" />
  </svg>
);

/* ── Вспомогательные ───────────────────────────────────── */
async function downloadFile(url, filename = "document.pdf") {
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function getPdf(row) {
  const url = row?.act_pdf_url || row?.pdf_url || null;
  const num = row?.act_no;
  const dateStr = row?.created_at
    ? new Date(row.created_at).toISOString().slice(0, 10)
    : "";
  const label = num ? `№${num}` : "PDF";
  const filename = num
    ? `Акт_${num}.pdf`
    : `Акт_${row?.assignment_id || row?.id || dateStr || "document"}.pdf`;
  return { url, label, filename };
}

/* ── Страница ──────────────────────────────────────────── */
export default function AssignmentsPage() {
  const [q, setQ] = useState("");
  // const [allRows, setAllRows] = useState([]); // Removed as we paginate server-side
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("assignmentsViewMode") || "grid"
  );

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    () => Number(localStorage.getItem("assignmentsPageSize")) || 12
  );
  const [sortBy, setSortBy] = useState("created_at");

  const [count, setCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  const [openAssign, setOpenAssign] = useState(false);
  const [openReturn, setOpenReturn] = useState(false);
  const [current, setCurrent] = useState(null);

  const [showConsumableConfirm, setShowConsumableConfirm] = useState(false);
  const [pendingReturn, setPendingReturn] = useState(null);

  useEffect(() => {
    localStorage.setItem("assignmentsViewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("assignmentsPageSize", String(pageSize));
    setPage(1);
  }, [pageSize]);

  const fetchAllRows = useCallback(async (searchQuery, currentPage = 1, currentSize = 12) => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * currentSize;
      const { rows: data, count: totalCount } = await listActiveAssignmentsApp({
        q: searchQuery,
        limit: currentSize,
        offset
      });

      const normalized = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        returned_qty: Number(r.returned_qty || 0),
        qty: Number(r.qty || 0),
      }));
      setRows(normalized); // Set current page rows directly
      setCount(totalCount || 0);
    } catch (e) {
      console.error("Failed to fetch assignments:", e.message);
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const debouncedFetch = useCallback(
    debounce((...args) => fetchAllRows(...args), 300),
    [fetchAllRows]
  );

  useEffect(() => {
    // Reset page on search change
    setPage(1);
    debouncedFetch(q, 1, pageSize);
    return () => debouncedFetch.cancel?.();
  }, [q, pageSize, debouncedFetch]);

  useEffect(() => {
    // Fetch on page change (skip if page=1 & just searched, handled by effect above effectively or can separate)
    // Simpler: just fetch when page changes
    debouncedFetch(q, page, pageSize);
  }, [page, debouncedFetch, pageSize]);

  // Sorting is now limited to current page (client-side) or needs backend sort support
  // For now, sorting only current page rows
  let sorted = [...rows];
  if (sortBy === "created_at") {
    sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  } else if (sortBy === "quantity") {
    sorted.sort((a, b) => b.qty - a.qty);
  } else {
    sorted.sort((a, b) => {
      const vA = String(a[sortBy] || "").toLowerCase();
      const vB = String(b[sortBy] || "").toLowerCase();
      return vA.localeCompare(vB);
    });
  }
  // We don't slice anymore, rows ARE the slice
  // setRows(sorted); // Problem: infinite loop if we setRows in dependency of rows
  // Solution: Sort in render or use memo


  const refreshData = () => fetchAllRows(q, page, pageSize);

  const handleReturnClick = (record) => {
    if (record.item_is_consumable) {
      setPendingReturn(record);
      setShowConsumableConfirm(true);
    } else {
      // пробрасываем id и остатки
      setCurrent({ ...record, id: record.assignment_id });
      setOpenReturn(true);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-screen-xl mx-auto space-y-6">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
              Topshiriqlar
            </h1>
            <p className="mt-1 text-md text-gray-600 dark:text-slate-400">
              Berilgan mahsulotlar va qaytarishlarni boshqarish.
            </p>
          </div>
          <Button
            onClick={() => setOpenAssign(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
          >
            <PlusIcon />
            <span>Mahsulot berish</span>
          </Button>
        </header>

        <div className="flex flex-col md:flex-row items-center gap-4 flex-wrap">
          <div className="relative w-full md:flex-1 md:min-w-[250px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 dark:text-slate-500">
                <SearchIcon />
              </span>
            </div>
            <input
              type="text"
              placeholder="Mahsulot yoki xodim bo'yicha qidiruv..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
            />
          </div>
          <div className="hidden md:flex flex-grow" />
          <select
            className="w-full md:w-auto bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-gray-900 dark:text-slate-100"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="created_at">Saralash: Berilgan sana</option>
            <option value="item_name">Saralash: Mahsulot</option>
            <option value="app_user_name">Saralash: Xodim</option>
            <option value="quantity">Saralash: Miqdor</option>
          </select>
          <PageSizeSelector value={pageSize} onChange={setPageSize} />
          <ViewSwitcher currentView={viewMode} onChange={setViewMode} />
        </div>

        <div>
          {loading ? (
            viewMode === "grid" ? (
              <SkeletonGrid pageSize={pageSize} />
            ) : (
              <SkeletonTable pageSize={pageSize} />
            )
          ) : rows.length === 0 ? (
            <EmptyState onCreate={() => setOpenAssign(true)} />
          ) : (
            <>
              {viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {rows.map((row) => (
                    <AssignmentCard
                      key={row.assignment_id}
                      row={row}
                      onReturn={handleReturnClick}
                    />
                  ))}
                </div>
              ) : (
                <AssignmentsTable rows={rows} onReturn={handleReturnClick} />
              )}
              {count > pageSize && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalCount={count}
                  pageSize={pageSize}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>

      <AssignModal
        open={openAssign}
        onClose={() => setOpenAssign(false)}
        onCreated={refreshData}
      />
      <ReturnModal
        open={openReturn}
        onClose={() => setOpenReturn(false)}
        assignment={current}
        onReturned={refreshData}
      />

      <ConfirmDialog
        open={showConsumableConfirm}
        onClose={() => {
          setShowConsumableConfirm(false);
          setPendingReturn(null);
        }}
        title="Sarflanuvchi material"
        message="Bu mahsulot sarflanuvchi material sifatida belgilangan. Odatda bunday mahsulotlar qaytarilmaydi. Davom etishni xohlaysizmi?"
        okText="Baribir qaytarish"
        cancelText="Bekor qilish"
        onOk={() => {
          setShowConsumableConfirm(false);
          if (pendingReturn) {
            setCurrent({ ...pendingReturn, id: pendingReturn.assignment_id });
            setOpenReturn(true);
            setPendingReturn(null);
          }
        }}
      />
    </div>
  );
}

/* ── Кастомные компоненты ──────────────────────────────── */
const AssignmentCard = ({ row, onReturn }) => {
  const { url, label, filename } = getPdf(row);
  const name = row?.full_name || "—";
  const email = row?.email || "—";
  const userHref = row?.app_user_id ? `/users/${row.app_user_id}` : undefined;

  const remaining = useMemo(
    () => Math.max(0, Number(row.qty || 0) - Number(row.returned_qty || 0)),
    [row.qty, row.returned_qty]
  );

  return (
    <div className="group relative bg-white dark:bg-slate-800 rounded-xl border border-gray-200/75 dark:border-slate-700 shadow-sm transition-all duration-300 hover:shadow-xl hover:border-blue-500">
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={name} />
            <div className="min-w-0">
              {userHref ? (
                <Link
                  className="font-semibold text-gray-800 dark:text-slate-100 hover:text-blue-600 transition-colors truncate block"
                  to={userHref}
                >
                  {name}
                </Link>
              ) : (
                <span className="font-semibold text-gray-800 dark:text-slate-100 truncate block">
                  {name}
                </span>
              )}
              <p className="text-sm text-gray-500 dark:text-slate-400 truncate">{email}</p>
            </div>
          </div>
          <PdfActions url={url} label={label} filename={filename} />
        </div>

        <div className="border-t border-gray-100 dark:border-slate-700 pt-4 space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-800 dark:text-slate-100 flex items-center gap-2 min-w-0" title={row?.item_name}>
              <span className="truncate">{row?.item_name || "—"}</span>
              {row?.item_is_consumable && (
                <span className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-800 shrink-0">
                  Sarflanuvchi
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-mono truncate">
              {row?.item_sku || "—"}
            </p>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="font-mono font-bold text-2xl text-gray-800 dark:text-slate-100">
                {remaining}{" "}
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  {row?.item_unit || "dona"}
                </span>
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400 -mt-0.5">
                Jami berilgan: {row?.qty ?? "—"} / Qaytarilgan:{" "}
                {row?.returned_qty ?? 0}
              </p>
            </div>
            <p className="text-xs text-gray-400 dark:text-slate-500">
              Berilgan:{" "}
              {row?.created_at
                ? new Date(row.created_at).toLocaleDateString()
                : "—"}
            </p>
          </div>
        </div>
      </div>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <ActionsMenu onReturn={() => onReturn(row)} disabled={remaining <= 0} />
      </div>
    </div>
  );
};

const AssignmentsTable = ({ rows, onReturn }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200/75 dark:border-slate-700 bg-white dark:bg-slate-800">
    <table className="w-full">
      <thead className="bg-gray-50/75 dark:bg-slate-700/50 border-b border-gray-200/75 dark:border-slate-700">
        <tr className="text-left text-xs uppercase text-gray-500 dark:text-slate-400 tracking-wider">
          <th className="py-3 px-6 font-semibold text-sm w-1/3">
            Foydalanuvchi
          </th>
          <th className="py-3 px-6 font-semibold text-sm w-1/3">Mahsulot</th>
          <th className="py-3 px-6 font-semibold text-sm">
            Qaytarilishi kerak bo'lgan qoldiq
          </th>
          <th className="py-3 px-6 font-semibold text-sm">Sana</th>
          <th className="py-3 px-6 font-semibold text-sm text-center">
            Amallar
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
        {rows.map((row) => {
          const { url, label, filename } = getPdf(row);
          const userHref = row?.app_user_id
            ? `/users/${row.app_user_id}`
            : undefined;
          const remaining = Math.max(
            0,
            Number(row.qty || 0) - Number(row.returned_qty || 0)
          );

          return (
            <tr
              key={row.assignment_id}
              className="group relative bg-white dark:bg-slate-800 transition-shadow hover:shadow-md"
            >
              <td className="py-4 px-6">
                {userHref ? (
                  <Link
                    className="font-semibold text-gray-800 dark:text-slate-100 hover:text-blue-600 transition-colors"
                    to={userHref}
                  >
                    {row?.full_name || "—"}
                  </Link>
                ) : (
                  <span className="font-semibold text-gray-800 dark:text-slate-100">
                    {row?.full_name || "—"}
                  </span>
                )}
                <p className="text-sm text-gray-500 dark:text-slate-400">{row?.email || "—"}</p>
              </td>
              <td className="py-4 px-6">
                <p className="font-medium text-gray-800 dark:text-slate-100 flex items-center gap-2 max-w-xs" title={row?.item_name}>
                  <span className="truncate">{row?.item_name || "—"}</span>
                  {row?.item_is_consumable && (
                    <span className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-800 shrink-0">
                      Sarflanuvchi
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-500 dark:text-slate-400 font-mono truncate max-w-xs">
                  {row?.item_sku || "—"}
                </p>
              </td>
              <td className="py-4 px-6 font-mono font-bold text-lg text-gray-800 dark:text-slate-100">
                {remaining}{" "}
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  {row?.item_unit || "dona"}
                </span>
                <div className="text-xs text-gray-400 dark:text-slate-500">
                  berilgan: {row?.qty ?? "—"} • qaytarilgan:{" "}
                  {row?.returned_qty ?? 0}
                </div>
              </td>
              <td className="py-4 px-6 text-gray-500 dark:text-slate-400">
                {row?.created_at
                  ? new Date(row.created_at).toLocaleDateString()
                  : "—"}
              </td>
              <td className="py-4 px-6">
                <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <PdfActions url={url} label={label} filename={filename} />
                  <ActionsMenu
                    onReturn={() => onReturn(row)}
                    disabled={remaining <= 0}
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const ActionsMenu = ({ onReturn, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 192;
      let left = rect.right + window.scrollX - menuWidth;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8)
        left = window.innerWidth - menuWidth - 8;
      setPosition({ top: rect.bottom + window.scrollY + 8, left });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        disabled={disabled}
        className="p-2 rounded-full text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:text-gray-300 dark:disabled:text-slate-600 disabled:hover:bg-transparent transition-colors"
        aria-label="Amallar"
        title={disabled ? "Qaytarish uchun hech narsa yo'q" : "Amallar"}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
            className="fixed w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl ring-1 ring-black/5 dark:ring-slate-700 z-50 p-2 border dark:border-slate-700"
          >
            <MenuItem
              onClick={onReturn}
              icon={<ReturnIcon />}
              disabled={disabled}
            >
              Оформить возврат
            </MenuItem>
          </div>,
          document.body
        )}
    </>
  );
};

const MenuItem = ({ children, icon, onClick, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${disabled
      ? "text-gray-300 dark:text-slate-600 cursor-not-allowed"
      : "text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
      }`}
  >
    <span className="opacity-70">{icon}</span>
    <span>{children}</span>
  </button>
);

function PdfActions({ url, label = "PDF", filename }) {
  if (!url) return <span className="text-xs text-gray-400 dark:text-slate-500 select-none">—</span>;
  const name = filename || `${label}.pdf`;
  return (
    <div className="inline-flex items-center gap-2">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
        title="Открыть PDF акта"
        onClick={(e) => e.stopPropagation()}
      >
        <PdfIcon />
        {label}
      </a>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          downloadFile(url, name);
        }}
        className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
        title="Скачать PDF"
      >
        <DownloadIcon />
        Скачать
      </button>
    </div>
  );
}

const Avatar = ({ name }) => {
  const initials =
    (name || "")
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 font-bold rounded-full text-md">
      {initials}
    </div>
  );
};

const EmptyState = ({ onCreate }) => (
  <div className="py-20 text-center bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-700 transition-colors hover:border-blue-500">
    <svg
      className="mx-auto h-12 w-12 text-gray-400 dark:text-slate-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 6.75h7.5M8.25 12h7.5m-7.5 5.25h7.5m3-13.5H3.75a1.5 1.5 0 00-1.5 1.5v14.25a1.5 1.5 0 001.5 1.5h16.5a1.5 1.5 0 001.5-1.5V8.25l-4.5-4.5z"
      />
    </svg>
    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-slate-100">
      Нет активных выдач
    </h3>
    <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
      Выдайте первый товар, чтобы он появился в этом списке.
    </p>
    <div className="mt-6">
      <Button
        onClick={onCreate}
        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white shadow-sm"
        title="Выдать товар"
      >
        <PlusIcon /> <span className="hidden sm:inline ml-2">Выдать товар</span>
      </Button>
    </div>
  </div>
);

const SkeletonGrid = ({ pageSize }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 animate-pulse">
    {[...Array(pageSize)].map((_, i) => (
      <div
        key={i}
        className="bg-white dark:bg-slate-800 p-5 rounded-xl border border-gray-200/75 dark:border-slate-700 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-slate-700" />
            <div className="w-full space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-32" />
              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-24" />
            </div>
          </div>
          <div className="h-6 w-12 bg-gray-200 dark:bg-slate-700 rounded-md" />
        </div>
        <div className="border-t border-gray-100 dark:border-slate-700 pt-4 space-y-3">
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-4/5" />
            <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-2/5" />
          </div>
          <div className="flex items-end justify-between">
            <div className="space-y-1">
              <div className="h-6 bg-gray-300 dark:bg-slate-600 rounded w-12" />
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-8" />
            </div>
            <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-16" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SkeletonTable = ({ pageSize }) => (
  <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 animate-pulse">
    <div className="h-12 bg-gray-50/75 dark:bg-slate-700/50 border-b border-gray-200/75 dark:border-slate-700/75" />
    <div className="divide-y divide-gray-100 dark:divide-slate-700">
      {[...Array(pageSize)].map((_, i) => (
        <div key={i} className="flex items-center space-x-6 h-16 px-6">
          <div className="w-1/3 space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/5" />
            <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-2/5" />
          </div>
          <div className="w-1/3 space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-4/5" />
            <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-3/5" />
          </div>
          <div className="w-[10%] h-6 bg-gray-300 dark:bg-slate-600 rounded" />
          <div className="w-[10%] h-4 bg-gray-200 dark:bg-slate-700 rounded" />
          <div className="w-[10%] flex justify-end gap-2">
            <div className="h-6 w-12 bg-gray-200 dark:bg-slate-700 rounded-md" />
            <div className="h-8 w-8 bg-gray-200 dark:bg-slate-700 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

function ViewSwitcher({ currentView, onChange }) {
  return (
    <div className="flex items-center p-1 bg-gray-200 dark:bg-slate-700 rounded-lg">
      <button
        onClick={() => onChange("grid")}
        className={`p-1.5 rounded-md transition-colors ${currentView === "grid"
          ? "bg-white dark:bg-slate-600 shadow-sm text-gray-900 dark:text-slate-100"
          : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
          }`}
        title="Сетка"
      >
        <GridIcon />
      </button>
      <button
        onClick={() => onChange("table")}
        className={`p-1.5 rounded-md transition-colors ${currentView === "table"
          ? "bg-white dark:bg-slate-600 shadow-sm text-gray-900 dark:text-slate-100"
          : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
          }`}
        title="Таблица"
      >
        <ListIcon />
      </button>
    </div>
  );
}

function PageSizeSelector({ value, onChange }) {
  const options = [12, 24, 48, 96];
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 dark:text-slate-400 hidden lg:block">
        Показывать по:
      </span>
      <select
        className="w-full md:w-auto bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-sm text-gray-900 dark:text-slate-100"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {options.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  );
}

const Pagination = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);
  return (
    <div className="mt-8 flex flex-col sm:flex-row items-center justify-between text-sm gap-4">
      <p className="text-gray-600 dark:text-slate-400 text-center sm:text-left">
        Показано <span className="font-medium text-gray-900 dark:text-slate-200">{from}</span>-
        <span className="font-medium text-gray-900 dark:text-slate-200">{to}</span> из{" "}
        <span className="font-medium text-gray-900 dark:text-slate-200">{totalCount}</span>
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className="bg-white dark:bg-slate-800 dark:border-slate-700 text-gray-700 dark:text-slate-200 disabled:text-gray-300 dark:disabled:text-slate-600"
          title="Назад"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
        <span className="text-gray-600 dark:text-slate-400 px-2">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className="bg-white dark:bg-slate-800 dark:border-slate-700 text-gray-700 dark:text-slate-200 disabled:text-gray-300 dark:disabled:text-slate-600"
          title="Вперёд"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
      </div>
    </div>
  );
};
